import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import ScheduleSelect from '../../Schedule/ScheduleSelect';
import '../../../component-styles/AddToScheduleModal.css';
import API_BASE from '../../../utils/api.js';
import { BUILT_IN_SCHEDULE_STATUSES, withCustomStatuses } from '../../../utils/orderStatuses.js';

function formatDimensions(dimensions) {
  if (!dimensions) return '';
  const parts = [];
  if (dimensions.width) parts.push(`${dimensions.width}"W`);
  if (dimensions.length) parts.push(`${dimensions.length}"L`);
  if (dimensions.height) parts.push(`${dimensions.height}"H`);
  if (dimensions.depth) parts.push(`${dimensions.depth}"D`);
  return parts.join(' x ');
}

export default function AddToScheduleModal({
  it,
  rooms = [],
  userId,
  projectId,
  invoiceId,
  invoiceNumber,
  onClose,
  onAdded,
}) {
  const [room, setRoom] = useState(it.room || '');
  const [scheduleId, setScheduleId] = useState(it.scheduleId || '');
  const [schedules, setSchedules] = useState([]);
  const [qty, setQty] = useState(it.qty || 1);
  const [unitType, setUnitType] = useState(it.unitType || 'Each');
  const [leadTime, setLeadTime] = useState('');
  const [status, setStatus] = useState('Ordered');
  const [customOrderStatuses, setCustomOrderStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const settings = await res.json();
        setCustomOrderStatuses(
          Array.isArray(settings?.customOrderStatuses) ? settings.customOrderStatuses : []
        );
      } catch {
        // non-fatal — falls back to the built-in statuses
      }
    })();
  }, [userId]);

  useEffect(() => {
    if (!userId || !projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setSchedules(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch schedules:', err);
      }
    })();
  }, [userId, projectId]);

  async function handleSave() {
    if (!room) return setError('Please select a room.');

    setSaving(true);
    setError(null);

    try {
      const createRes = await fetch(`${API_BASE}/api/schedule/${userId}/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: it.clientName || it.name,
          description: it.description || '',
          imageUrl: it.imageUrl || '',
          room,
          scheduleType: 'ffe',
          scheduleId,
          vendor: it.vendorName || it.vendor || '',
          measurement: formatDimensions(it.dimensions),
          qty,
          unitType,
          leadTime,
          status,
          linkedInvoiceId: invoiceId,
          linkedInvoiceNumber: invoiceNumber || '',
          linkedLineItemId: it.itemId,
        }),
      });

      if (!createRes.ok) throw new Error('Failed to create schedule item.');
      const created = await createRes.json();

      const linkRes = await fetch(
        `${API_BASE}/api/invoices/${userId}/${projectId}/${invoiceId}/line-items/${it.itemId}/schedule-link`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scheduleItemId: created.sk }),
        }
      );
      if (!linkRes.ok) throw new Error('Failed to link item to schedule.');

      onAdded(created.sk);
    } catch (err) {
      console.error('Add to schedule error:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="ats-modal-overlay" onClick={onClose}>
      <div className="ats-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ats-modal__heading">
          <Calendar size={18} />
          Add to Schedule
        </h3>

        <div className="ats-modal__row">
          <div className="ats-modal__field">
            <label className="ats-modal__label">Room</label>
            <select
              className="ats-modal__input"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
            >
              <option value="">Select a room</option>
              {rooms.map((r) => (
                <option key={r.sectionId || r.name} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ats-modal__field">
            <label className="ats-modal__label">Schedule</label>
            <ScheduleSelect
              schedules={schedules}
              value={scheduleId}
              onChange={setScheduleId}
              userId={userId}
              projectId={projectId}
              onScheduleCreated={(s) => setSchedules((prev) => [...prev, s])}
              selectClassName="ats-modal__input"
            />
          </div>
        </div>

        <div className="ats-modal__row">
          <div className="ats-modal__field">
            <label className="ats-modal__label">Qty</label>
            <input
              className="ats-modal__input"
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div className="ats-modal__field">
            <label className="ats-modal__label">Unit</label>
            <input
              className="ats-modal__input"
              type="text"
              value={unitType}
              onChange={(e) => setUnitType(e.target.value)}
              placeholder="e.g., pcs, set, box, lf, sq ft, yd"
            />
          </div>
        </div>

        <div className="ats-modal__row">
          <div className="ats-modal__field">
            <label className="ats-modal__label">Lead Time</label>
            <input
              className="ats-modal__input"
              type="text"
              value={leadTime}
              onChange={(e) => setLeadTime(e.target.value)}
              placeholder="e.g., 2 weeks, 3-4 weeks, 30 days"
            />
          </div>

          <div className="ats-modal__field">
            <label className="ats-modal__label">Status</label>
            <select
              className="ats-modal__input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {withCustomStatuses(BUILT_IN_SCHEDULE_STATUSES, customOrderStatuses, status).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="ats-modal__error">{error}</div>}

        <div className="ats-modal__actions">
          <button type="button" className="ats-modal__cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ats-modal__save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save & Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
